/**
 * lib/tableExport.ts
 * ------------------------------------------------------------------
 * THE ONE PLACE A SCREEN GETS A PDF OR A CSV.
 *
 * Any screen that puts figures in a table can hand them here and get back the
 * school's letterhead, the gold rule, zebra rows, a totals band and a stamped
 * footer — the same chrome the academic Analysis and Comment Review exports
 * use (lib/pdfChrome), so a bursar's cash-flow statement and a head of year's
 * marksheet are recognisably the same school's paperwork.
 *
 * WHY CENTRALISED. There were already two dozen bespoke PDF builders in this
 * codebase and each new one drifted a little further from the last: different
 * margins, different date formats, someone's own idea of a header. Every screen
 * that only needs "these columns, these rows, printed properly" should not be
 * writing jsPDF at all.
 *
 * CSV IS NOT AN AFTERTHOUGHT. Finance staff live in spreadsheets, and a figure
 * they cannot get out of the system is a figure they will keep in a parallel
 * one. Both formats come from the SAME column definitions, so the export a
 * bursar opens in Excel always carries the same numbers as the PDF they filed.
 *
 * The value a column shows and the value it exports are deliberately separate:
 * `text` is what the reader sees (P42,000.00), `raw` is what a spreadsheet
 * should receive (42000) — a currency-formatted string in a CSV is a number
 * nobody can sum.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import autoTable, { type RowInput, type Styles } from 'jspdf-autotable';
import {
  PDF, CONTENT_TOP, pageGeom, drawLetterhead, stampFooters, type PageGeom,
} from '@/lib/pdfChrome';

export type Align = 'left' | 'right' | 'center';

export interface ExportColumn<T> {
  /** Column heading, in both the PDF and the CSV. */
  header: string;
  /** What the reader sees. Formatted — currency, dates, labels. */
  text: (row: T) => string;
  /** What a spreadsheet gets. Defaults to `text`; give a number for money. */
  raw?: (row: T) => string | number | null;
  align?: Align;
  /** Column width in mm. Omit to let the table size itself. */
  width?: number;
}

export interface ExportSection<T> {
  /** Optional band above this block of rows — a class, a supplier, a month. */
  heading?: string;
  rows: T[];
}

export interface TableExport<T> {
  /** Printed at the top right, e.g. 'CASH FLOW STATEMENT'. */
  title: string;
  /** The line under it: period, filter, whatever makes the figures reproducible. */
  meta?: string;
  columns: ExportColumn<T>[];
  rows?: T[];
  /** Use instead of `rows` when the table is grouped. */
  sections?: ExportSection<T>[];
  /** A closing band — totals, a count, a balance. Same column order. */
  totals?: (string | number)[];
  orientation?: 'p' | 'l';
  /** Free text printed under the table: a caveat, a basis of preparation. */
  note?: string;
  generatedBy?: string;
  /** File name without extension. Defaults to a slug of the title. */
  filename?: string;
}

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'export';

const stamp = () => new Date().toISOString().slice(0, 10);

/* ============================== the PDF ============================== */

/** Build the document. Separated from `download` so a caller can bundle it. */
export function buildTablePdf<T>(x: TableExport<T>): jsPDF {
  const orientation = x.orientation ?? 'p';
  const geom: PageGeom = pageGeom(orientation);
  const d = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

  drawLetterhead(d, { title: x.title.toUpperCase(), metaLine: x.meta, geom });

  const head = [x.columns.map((c) => c.header)];
  const body: RowInput[] = [];

  const push = (rows: T[]) => rows.forEach((r) => body.push(x.columns.map((c) => c.text(r))));

  if (x.sections?.length) {
    x.sections.forEach((s) => {
      if (s.heading) {
        // A heading row spanning the table, so a grouped export reads as
        // groups rather than as one undifferentiated list.
        body.push([{ content: s.heading, colSpan: x.columns.length, styles: { fontStyle: 'bold', fillColor: PDF.BAND, textColor: PDF.NAVY } }]);
      }
      push(s.rows);
    });
  } else {
    push(x.rows ?? []);
  }

  if (x.totals) {
    body.push(x.totals.map((t) => ({
      content: String(t),
      styles: { fontStyle: 'bold', fillColor: PDF.BAND, textColor: PDF.NAVY } as Partial<Styles>,
    })));
  }

  const columnStyles: Record<number, Partial<Styles>> = {};
  x.columns.forEach((c, i) => {
    columnStyles[i] = {
      halign: c.align ?? 'left',
      ...(c.width ? { cellWidth: c.width } : {}),
    };
  });

  autoTable(d, {
    head,
    body,
    startY: CONTENT_TOP,
    margin: { left: geom.MX, right: geom.MX, top: CONTENT_TOP },
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 8, cellPadding: 1.6, lineColor: PDF.RULE, lineWidth: 0.1, textColor: PDF.INK },
    headStyles: { fillColor: PDF.NAVY, textColor: PDF.WHITE, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: PDF.ZEBRA },
    columnStyles,
    // Redraw the letterhead on every page — a page two that arrives on a desk
    // without the school's name on it is not a document anyone can file.
    didDrawPage: () => drawLetterhead(d, { title: x.title.toUpperCase(), metaLine: x.meta, geom }),
  });

  if (x.note) {
    const afterY = (d as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? CONTENT_TOP;
    d.setFont('helvetica', 'italic');
    d.setFontSize(7.5);
    d.setTextColor(...PDF.MUTED);
    d.text(d.splitTextToSize(x.note, geom.right - geom.MX), geom.MX, Math.min(afterY + 6, geom.PH - 16));
  }

  stampFooters(d, { geom, generatedBy: x.generatedBy });
  return d;
}

/** Build and save. The screen's PDF button is one call to this. */
export function downloadTablePdf<T>(x: TableExport<T>): void {
  buildTablePdf(x).save(`${x.filename ?? slug(x.title)}-${stamp()}.pdf`);
}

/* ============================== the CSV ============================== */

/**
 * RFC-4180 quoting, and a leading apostrophe stripped from nothing — but note
 * the deliberate absence of a BOM-less write: Excel misreads plain UTF-8 and a
 * pupil called Björk arrives as BjÃ¶rk, so the BOM stays.
 */
const cell = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCsv<T>(columns: ExportColumn<T>[], rows: T[]): string {
  const head = columns.map((c) => cell(c.header)).join(',');
  const body = rows.map((r) => columns.map((c) => cell(c.raw ? c.raw(r) : c.text(r))).join(','));
  return [head, ...body].join('\r\n');
}

export function downloadCsv<T>(x: TableExport<T>): void {
  const rows = x.sections?.length ? x.sections.flatMap((s) => s.rows) : (x.rows ?? []);
  const csv = toCsv(x.columns, rows);
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${x.filename ?? slug(x.title)}-${stamp()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoked on the next tick: revoking synchronously races the download in
  // Safari and silently produces an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
